import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Alert,
  TablePagination,
  Checkbox,
  ListItemText,
  Menu,
  ListItemIcon,
  Tooltip,
  Skeleton
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Restore as RestoreIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  HelpOutline as HelpOutlineIcon,
  PinDrop,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { customerService, Customer, CustomerSearchDto, CreateCustomerDto, UpdateCustomerDto, CustomerSource, CustomerStatus, CustomerOrderBy } from '../services/customerService';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessTheme } from '../contexts/BusinessThemeContext';
import { offlineService } from '../services/offlineService';
import { indexedDbService } from '../services/indexedDbService';
import BaseDialog from '../components/common/BaseDialog';
import CustomerDetailsCard from '../components/common/CustomerDetailsCard';
import JobForm from '../components/common/JobForm';
import CustomerFormDialog from '../components/common/CustomerFormDialog';
import { businessService, Business } from '../services/businessService';
import { jobService, Job, CreateJobDto, JobType, Priority } from '../services/jobService';
import { Helmet } from 'react-helmet-async';
import { useCustomers, useCustomerCount, useUpdateCustomer, useDeleteCustomer } from '../hooks/useCustomers';
import { useCreateJob } from '../hooks/useJobs';
import { useBusinessUsers } from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../config/queryKeys';
import { extractApiErrorMessage } from '../utils/apiUtils';
import { sortUsers } from '../utils/userHelpers';

const CustomersPage: React.FC = () => {
  const { user } = useAuth();
  const { theme } = useBusinessTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL parameters on mount
  const initialSearchTerm = searchParams.get('search') || '';
  const initialSalesPersonIds = searchParams.get('salesPersonIds')?.split(',').filter(Boolean) || [];
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const initialPageSize = parseInt(searchParams.get('pageSize') || '20', 10);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!offlineService.getOnlineStatus());
  const [searchDto, setSearchDto] = useState<CustomerSearchDto>({
    page: initialPage,
    pageSize: initialPageSize,
    orderBy: CustomerOrderBy.RecentActivity,
    searchTerm: initialSearchTerm || undefined,
    assignedSalesPersonId: initialSalesPersonIds.length === 1 ? initialSalesPersonIds[0] : undefined
  });
  const [localSearchTerm, setLocalSearchTerm] = useState(initialSearchTerm);
  const [mainSearchTimeout, setMainSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [selectedSalesPersonIds, setSelectedSalesPersonIds] = useState<string[]>(initialSalesPersonIds);
  const [page, setPage] = useState(initialPage - 1);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  // Update URL when filters change
  const updateUrlParams = useCallback((params: {
    search?: string;
    salesPersonIds?: string[];
    page?: number;
    pageSize?: number;
  }) => {
    const newParams = new URLSearchParams();

    if (params.search) {
      newParams.set('search', params.search);
    }
    if (params.salesPersonIds && params.salesPersonIds.length > 0) {
      newParams.set('salesPersonIds', params.salesPersonIds.join(','));
    }
    if (params.page && params.page > 1) {
      newParams.set('page', params.page.toString());
    }
    if (params.pageSize && params.pageSize !== 20) {
      newParams.set('pageSize', params.pageSize.toString());
    }

    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  const [business, setBusiness] = useState<Business | null>(null);

  // Business users for sales person filter
  const { data: businessUsersData } = useBusinessUsers({ businessId: user?.businessId ?? undefined });
  const businessUsers = useMemo(() => sortUsers(businessUsersData || []), [businessUsersData]);

  // Offline data state
  const [offlineCustomers, setOfflineCustomers] = useState<Customer[]>([]);
  const [offlineLoading, setOfflineLoading] = useState(false);

  // TanStack Query hooks - only enabled when online
  const {
    data: onlineCustomers = [],
    isLoading: customersLoading,
    isFetching: customersFetching,
    isStale: customersStale,
    isPlaceholderData: customersPlaceholderData,
    error: customersError
  } = useCustomers({
    ...searchDto,
    enabled: !isOffline && !!user?.businessId
  });

  // Debug logging for stale-while-revalidate behavior
  useEffect(() => {
    console.log('[CustomersPage] Query state:', {
      isLoading: customersLoading,
      isFetching: customersFetching,
      isStale: customersStale,
      isPlaceholderData: customersPlaceholderData,
      hasData: onlineCustomers.length > 0,
      dataCount: onlineCustomers.length,
      searchDto
    });
  }, [customersLoading, customersFetching, customersStale, customersPlaceholderData, onlineCustomers.length, searchDto]);

  const {
    data: onlineTotalCount = 0,
    isLoading: countLoading
  } = useCustomerCount(isOffline ? undefined : searchDto);

  // Mutations
  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();
  const createJobMutation = useCreateJob();

  // Determine which data to use based on offline status
  // Apply client-side filtering for multiple sales person IDs (backend only supports single ID)
  const baseCustomers = isOffline ? offlineCustomers : onlineCustomers;
  const customers = useMemo(() => {
    if (selectedSalesPersonIds.length <= 1) {
      return baseCustomers;
    }
    // Filter for multiple selected sales persons client-side
    return baseCustomers.filter(customer =>
      customer.assignedSalesPersonId && selectedSalesPersonIds.includes(customer.assignedSalesPersonId)
    );
  }, [baseCustomers, selectedSalesPersonIds]);
  const totalCount = isOffline ? offlineCustomers.length : onlineTotalCount;

  // Show loading skeleton when:
  // - Initial load (customersLoading) OR count loading
  // - Showing placeholder data from previous query while fetching new search results (customersPlaceholderData)
  const loading = isOffline ? offlineLoading : (customersLoading || countLoading || customersPlaceholderData);

  // Job creation dialog state
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [selectedCustomerForJob, setSelectedCustomerForJob] = useState<Customer | null>(null);
  const [jobFormData, setJobFormData] = useState<CreateJobDto>({
    customerId: '',
    assignedSalesPersonId: user?.id || '',
    jobAddress: '',
    type: JobType.Consultation,
    estimatedValue: 0,
    priority: Priority.Medium,
    notes: ''
  });

  // Customer search for job dialog
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);

  // Menu state for mobile actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomerForMenu, setSelectedCustomerForMenu] = useState<Customer | null>(null);

  // Shared customer creation dialog handler
  const handleCustomerCreated = (customer: Customer, initialJob?: Job) => {
    setSuccess('Customer created');
    setError(null);
    // Invalidate queries to refetch
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.lists() });
    queryClient.invalidateQueries({ queryKey: queryKeys.customers.count() });
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs.all });
    // Close the dialog first
    setIsCreateDialogOpen(false);
    // Navigate to the customer detail page, with job data if created
    navigate(`/crm/customers/${customer.id}`, {
      state: initialJob ? { newJobData: initialJob } : undefined
    });
  };

  // Load offline data when offline
  useEffect(() => {
    if (isOffline && user?.businessId) {
      loadOfflineCustomers();
    }
  }, [isOffline, searchDto, user?.businessId]);

  const loadOfflineCustomers = async () => {
    if (!user?.businessId) return;

    try {
      setOfflineLoading(true);
      console.log('[CustomersPage] Loading customers from IndexedDB (offline mode)');

      const customersData = await indexedDbService.getCustomersForBusiness(user.businessId);

      // Apply local filtering
      let filteredCustomers = customersData;

      if (searchDto.searchTerm) {
        const searchTerm = searchDto.searchTerm.toLowerCase();
        filteredCustomers = customersData.filter(customer =>
          customer.firstName?.toLowerCase().includes(searchTerm) ||
          customer.lastName?.toLowerCase().includes(searchTerm) ||
          customer.email?.toLowerCase().includes(searchTerm) ||
          customer.phoneNumber?.includes(searchTerm) ||
          customer.address?.toLowerCase().includes(searchTerm) ||
          customer.assignedSalesPersonName?.toLowerCase().includes(searchTerm)
        );
      }

      if (searchDto.statuses && searchDto.statuses.length > 0) {
        filteredCustomers = filteredCustomers.filter(customer =>
          searchDto.statuses!.includes(customer.status as CustomerStatus)
        );
      }

      // Sort by name
      filteredCustomers = filteredCustomers.sort((a, b) => {
        const firstNameA = (a.firstName || '').toLowerCase();
        const firstNameB = (b.firstName || '').toLowerCase();
        const lastNameA = (a.lastName || '').toLowerCase();
        const lastNameB = (b.lastName || '').toLowerCase();

        if (firstNameA !== firstNameB) {
          return firstNameA.localeCompare(firstNameB);
        }
        return lastNameA.localeCompare(lastNameB);
      });

      // Limit to 20 results
      const limitedCustomers = filteredCustomers.slice(0, 20);

      setOfflineCustomers(limitedCustomers);
    } catch (err: any) {
      console.error('Error loading offline customers:', err);
      setError('Failed to load offline customers');
    } finally {
      setOfflineLoading(false);
    }
  };

  // Offline status monitoring
  useEffect(() => {
    const handleConnectivityChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newIsOffline = !customEvent.detail.isOnline;
      setIsOffline(newIsOffline);

      // When coming back online, invalidate queries to refetch
      if (!newIsOffline) {
        queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      }
    };

    window.addEventListener('connectivity-changed', handleConnectivityChange);

    return () => {
      window.removeEventListener('connectivity-changed', handleConnectivityChange);
    };
  }, [queryClient]);

  useEffect(() => {
    if (user?.businessId) {
      loadBusiness();
    }
  }, [user?.businessId]);

  // Stash updateUrlParams in a ref so the debounced filter effect below doesn't re-fire when
  // its identity changes. react-router-dom v6 recreates `setSearchParams` on every URL change,
  // which cascades through useCallback into a fresh `updateUrlParams` — if that were a real dep
  // of the debounced effect, every pagination click would re-trigger the effect and reset page
  // back to 1.
  const updateUrlParamsRef = useRef(updateUrlParams);
  useEffect(() => {
    updateUrlParamsRef.current = updateUrlParams;
  }, [updateUrlParams]);

  // Debounced main customer search — reacts only to *filter* changes.
  useEffect(() => {
    if (mainSearchTimeout) {
      clearTimeout(mainSearchTimeout);
    }

    const timeout = setTimeout(() => {
      setSearchDto(prev => ({
        ...prev,
        searchTerm: localSearchTerm || undefined,
        page: 1
      }));
      setPage(0);
      // Update URL params when search changes
      updateUrlParamsRef.current({
        search: localSearchTerm || undefined,
        salesPersonIds: selectedSalesPersonIds,
        page: 1,
        pageSize: rowsPerPage
      });
    }, 300);

    setMainSearchTimeout(timeout);

    return () => {
      clearTimeout(timeout);
    };
  }, [localSearchTerm, selectedSalesPersonIds, rowsPerPage]);

  // Debounced search effect for job dialog
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (customerSearchTerm.trim()) {
      const timeout = setTimeout(() => {
        handleSearchCustomers();
      }, 300);
      setSearchTimeout(timeout);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [customerSearchTerm]);

  // Auto-dismiss success messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle query errors
  useEffect(() => {
    if (customersError) {
      setError('Failed to load customers');
    }
  }, [customersError]);

  const loadBusiness = async () => {
    if (!user?.businessId) return;

    try {
      const businessData = await businessService.getBusiness(user.businessId);
      setBusiness(businessData);
    } catch (err: any) {
      console.error('Failed to load business:', err);
    }
  };

  const handleSearch = (field: keyof CustomerSearchDto, value: any) => {
    setSearchDto(prev => ({
      ...prev,
      [field]: value === '' || value === null ? undefined : value,
      page: 1
    }));
  };

  const handleSalesPersonChange = (salesPersonIds: string[]) => {
    setSelectedSalesPersonIds(salesPersonIds);
    setPage(0);
    setSearchDto(prev => ({
      ...prev,
      assignedSalesPersonId: salesPersonIds.length === 1 ? salesPersonIds[0] : undefined,
      page: 1
    }));
    // Update URL params when sales person filter changes
    updateUrlParams({
      search: localSearchTerm || undefined,
      salesPersonIds: salesPersonIds,
      page: 1,
      pageSize: rowsPerPage
    });
  };

  const handleCreateJob = async () => {
    try {
      await createJobMutation.mutateAsync(jobFormData);
      setIsJobDialogOpen(false);
      setSelectedCustomerForJob(null);
      setJobFormData({
        customerId: '',
        assignedSalesPersonId: user?.id || '',
        jobAddress: '',
        type: JobType.Consultation,
        estimatedValue: 0,
        priority: Priority.Medium,
        notes: ''
      });
      setSuccess('Job created');
      setError(null);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to create job'));
      setSuccess(null);
    }
  };

  const handleSearchCustomers = async () => {
    if (!customerSearchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const searchDto: CustomerSearchDto = {
        searchTerm: customerSearchTerm,
        page: 1,
        pageSize: 20
      };
      const results = await customerService.getCustomers(searchDto);
      setSearchResults(results);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to search customers'));
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomerForJob(customer);
    setJobFormData(prev => ({
      ...prev,
      customerId: customer.id,
      jobAddress: customer.address || ''
    }));
    setShowCustomerSearch(false);
    setSearchResults([]);
    setCustomerSearchTerm('');
  };

  const handleFieldFocus = () => {
    if (showCustomerSearch) {
      setShowCustomerSearch(false);
      setSearchResults([]);
      setCustomerSearchTerm('');
    }
  };

  // Menu handlers for mobile actions
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomerForMenu(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomerForMenu(null);
  };

  const handleMenuAction = (action: string) => {
    if (!selectedCustomerForMenu) return;

    switch (action) {
      case 'view':
        handleViewCustomer(selectedCustomerForMenu);
        break;
      case 'createJob':
        handleCreateJobForCustomer(selectedCustomerForMenu);
        break;
      case 'delete':
        handleDeleteCustomer(selectedCustomerForMenu.id);
        break;
      case 'reinstate':
        handleReinstateCustomer(selectedCustomerForMenu);
        break;
    }
    handleMenuClose();
  };

  const resetJobForm = () => {
    setJobFormData({
      customerId: '',
      assignedSalesPersonId: user?.id || '',
      jobAddress: '',
      type: JobType.Consultation,
      estimatedValue: 0,
      priority: Priority.Medium,
      notes: ''
    });
    setSelectedCustomerForJob(null);
    setCustomerSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
    setShowCustomerSearch(false);
    setError(null);
    setSuccess(null);
  };

  const handleDeleteCustomer = async (customerId: string) => {
    if (!window.confirm('Are you sure you want to mark this customer as inactive? This will remove them from the active customer list.')) return;

    try {
      await deleteCustomerMutation.mutateAsync(customerId);
      setSuccess('Customer marked as inactive successfully');
      setError(null);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to mark customer as inactive'));
    }
  };

  const handleReinstateCustomer = async (customer: Customer) => {
    if (!window.confirm(`Are you sure you want to reinstate ${customer.fullName}? This will mark them as active again.`)) return;

    try {
      const updateDto: UpdateCustomerDto = {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phoneNumber: customer.phoneNumber,
        address: customer.address,
        city: customer.city,
        country: customer.country,
        source: customer.source,
        status: CustomerStatus.Active,
        preferredCommunicationMethod: customer.preferredCommunicationMethod,
        notes: customer.notes
      };

      await updateCustomerMutation.mutateAsync(updateDto);
      setSuccess('Customer reinstated successfully');
      setError(null);
    } catch (err: any) {
      setError(extractApiErrorMessage(err, 'Failed to reinstate customer'));
    }
  };

  const handleCreateJobForCustomer = (customer: Customer) => {
    setSelectedCustomerForJob(customer);
    setJobFormData({
      customerId: customer.id,
      assignedSalesPersonId: user?.id || '',
      jobAddress: customer.address || '',
      type: JobType.Consultation,
      estimatedValue: 0,
      priority: Priority.Medium,
      notes: ''
    });
    setShowCustomerSearch(false);
    setError(null);
    setSuccess(null);
    setIsJobDialogOpen(true);
  };

  const handleViewCustomer = (customer: Customer) => {
    navigate(`/crm/customers/${customer.id}`);
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
    setSearchDto(prev => ({
      ...prev,
      page: newPage + 1
    }));
    // Update URL params when page changes
    updateUrlParams({
      search: localSearchTerm || undefined,
      salesPersonIds: selectedSalesPersonIds,
      page: newPage + 1,
      pageSize: rowsPerPage
    });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSearchDto(prev => ({
      ...prev,
      page: 1,
      pageSize: newRowsPerPage
    }));
    // Update URL params when page size changes
    updateUrlParams({
      search: localSearchTerm || undefined,
      salesPersonIds: selectedSalesPersonIds,
      page: 1,
      pageSize: newRowsPerPage
    });
  };


  return (
    <>
      <Helmet>
        <title>Customers | MindShare Calendar</title>
      </Helmet>

      <Box sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1">
              Customers
            </Typography>
          </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={isOffline}
          title={isOffline ? "Customer creation is not available offline" : "Add Customer"}
        >
          Add Customer
        </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Search and Filters */}
        <Card sx={{ mb: 3, backgroundColor: theme.accentColor }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  placeholder="Search customers..."
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Assigned Sales Person</InputLabel>
                  <Select
                    multiple
                    value={selectedSalesPersonIds}
                    onChange={(e) => handleSalesPersonChange(e.target.value as string[])}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    label="Assigned Sales Person"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((id) => {
                          const user = businessUsers.find(u => u.id === id);
                          return (
                            <Chip
                              key={id}
                              label={user ? `${user.firstName} ${user.lastName}` : id}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {businessUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        <Checkbox checked={selectedSalesPersonIds.indexOf(user.id) > -1} />
                        <ListItemText primary={`${user.firstName} ${user.lastName}`} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Customers Table */}
        <TableContainer component={Paper} sx={{ backgroundColor: theme.accentColor, position: 'relative' }}>
          {loading && (
            <Box sx={{ width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 1 }}>
              <Box sx={{ width: '100%', height: 4, bgcolor: 'primary.main', opacity: 0.7 }}>
                <Box
                  sx={{
                    height: '100%',
                    width: '30%',
                    bgcolor: 'primary.light',
                    animation: 'loading 1s ease-in-out infinite',
                    '@keyframes loading': {
                      '0%': { marginLeft: '0%' },
                      '50%': { marginLeft: '70%' },
                      '100%': { marginLeft: '0%' }
                    }
                  }}
                />
              </Box>
            </Box>
          )}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={`skeleton-${index}`}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Skeleton variant="circular" width={18} height={18} />
                        <Skeleton variant="text" width={150} height={24} />
                      </Box>
                      <Box sx={{ mt: 0.5 }}>
                        <Skeleton variant="text" width={200} height={20} />
                        <Skeleton variant="text" width={180} height={20} />
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end">
                        <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1 }} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Alert severity="info" sx={{ my: 2 }}>
                      No customers found. {searchDto.searchTerm || selectedSalesPersonIds.length > 0 ? 'Try adjusting your search filters or add a new customer.' : 'Click "Add Customer" to create your first customer.'}{' '}
                      <Button
                        color="inherit"
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setIsCreateDialogOpen(true)}
                        disabled={isOffline}
                        sx={{ ml: 1 }}
                      >
                        Add Customer
                      </Button>
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    hover
                    onClick={() => handleViewCustomer(customer)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box>
                        <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                          <Tooltip title={customer.statusDisplayName}>
                            {customer.status === CustomerStatus.Active || customer.status === 'Active' ? (
                              <CheckCircleIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                            ) : customer.status === CustomerStatus.Prospect || customer.status === 'Prospect' ? (
                              <HelpOutlineIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                            ) : (
                              <CancelIcon sx={{ fontSize: 18, mr: 1, color: 'grey.500' }} />
                            )}
                          </Tooltip>
                          <Typography variant="body2" fontWeight="medium">
                            {customer.fullName}
                          </Typography>
                          {customer.isDifficultPayer && (
                            <Tooltip title={customer.difficultPayerNotes || 'Difficult Payer'}>
                              <WarningAmberIcon sx={{ fontSize: 16, ml: 0.5, color: 'warning.main' }} />
                            </Tooltip>
                          )}
                        </Box>
                        {customer.email && (
                          <Box display="flex" alignItems="center" sx={{ mb: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {customer.email}
                            </Typography>
                          </Box>
                        )}
                        {customer.phoneNumber && (
                          <Box display="flex" alignItems="center">
                            <PhoneIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {customer.phoneNumber}
                            </Typography>
                          </Box>
                        )}
                        {customer.address && (
                          <Box display="flex" alignItems="center">
                            <PinDrop sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {customer.address}
                            </Typography>
                          </Box>
                        )}
                        {customer.assignedSalesPersonName && (
                          <Box display="flex" alignItems="center">
                            <BusinessIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              Salesperson: {customer.assignedSalesPersonName}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {/* Desktop Actions */}
                      <Box display={{ xs: 'none', sm: 'flex' }} gap={1} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCustomer(customer);
                          }}
                        >
                          View
                        </Button>
                        {customer.status === CustomerStatus.Inactive || customer.status === 'Inactive' ? (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReinstateCustomer(customer);
                            }}
                            color="success"
                            title="Mark as Active"
                            disabled={isOffline}
                          >
                            <RestoreIcon />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteCustomer(customer.id);
                            }}
                            color="error"
                            title="Mark as Inactive"
                            disabled={isOffline}
                            sx={{ display: 'none' }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>

                      {/* Mobile Actions - 3 dots menu */}
                      <Box display={{ xs: 'block', sm: 'none' }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, customer);
                          }}
                          color="primary"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </TableContainer>

        {/* Create Customer Dialog */}
        <CustomerFormDialog
          open={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSuccess={handleCustomerCreated}
          business={business}
          title="Create Customer"
          defaultAssignedSalesPersonId={user?.id}
          createInitialJob={true}
        />

        {/* Create Job Dialog */}
        <BaseDialog
          open={isJobDialogOpen}
          onClose={() => {
            setIsJobDialogOpen(false);
            resetJobForm();
          }}
          title={selectedCustomerForJob?.fullName?.trim() ? `Create Job for ${selectedCustomerForJob.fullName}` : 'Create Job'}
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: { xs: 'calc(100vw - 10px)', sm: '900px' }
            }
          }}
          contentSx={{ pb: 4 }}
          actions={
            <>
              <Button
                onClick={() => {
                  setIsJobDialogOpen(false);
                  resetJobForm();
                }}
                variant="outlined"
                color="primary"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateJob}
                variant="contained"
                disabled={createJobMutation.isPending}
              >
                {createJobMutation.isPending ? 'Creating...' : 'Create Job'}
              </Button>
            </>
          }
        >
            {error && <Alert severity="error" sx={{ mb: 2, mt: 1, whiteSpace: 'pre-line' }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{success}</Alert>}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Selected Customer Display - Always at top */}
              {selectedCustomerForJob && (
                <Grid item xs={12}>
                  <CustomerDetailsCard
                    customer={selectedCustomerForJob}
                    showChangeButton={false}
                  />
                </Grid>
              )}

              <JobForm
                formData={jobFormData}
                onFormDataChange={setJobFormData}
                defaultBounds={{
                  city: business?.defaultCity,
                  country: business?.defaultCountry
                }}
                onFieldFocus={handleFieldFocus}
                showCustomerSelection={false}
              />
            </Grid>
        </BaseDialog>

        {/* Mobile Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => handleMenuAction('view')}>
            <ListItemIcon>
              <AssignmentIcon fontSize="small" />
            </ListItemIcon>
            View Customer
          </MenuItem>
          {selectedCustomerForMenu && (selectedCustomerForMenu.status === CustomerStatus.Inactive || selectedCustomerForMenu.status === 'Inactive') ? (
            <MenuItem
              onClick={() => handleMenuAction('reinstate')}
              disabled={isOffline}
            >
              <ListItemIcon>
                <RestoreIcon fontSize="small" />
              </ListItemIcon>
              Mark as Active
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => handleMenuAction('delete')}
              disabled={isOffline}
              sx={{ display: 'none' }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              Mark as Inactive
            </MenuItem>
          )}
        </Menu>
      </Box>
    </>
  );
};

export default CustomersPage;
